/**
 * useDevice Hook
 *
 * Detects device type (mobile vs desktop) using CSS media query matching.
 * Returns isMobile boolean for responsive UI rendering.
 *
 * Part of Prompt 5: Narrative (desktop + mobile)
 */

import { useEffect, useState } from 'react';

export interface UseDeviceResult {
  isMobile: boolean;
  isDesktop: boolean;
}

/**
 * Hook to detect if the current viewport is mobile or desktop
 * Uses standard Tailwind breakpoint: 768px (md)
 */
export function useDevice(): UseDeviceResult {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Mobile breakpoint: < 768px (Tailwind's md breakpoint)
    const mediaQuery = window.matchMedia('(max-width: 767px)');

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Update on change
    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      // Legacy browsers
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, []);

  return {
    isMobile,
    isDesktop: !isMobile
  };
}
